import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight, MapPin, Clock, ShieldCheck, Star, UploadCloud, Sliders, Zap, Check } from "lucide-react";import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, RevealStagger, StaggerItem } from "@/components/motion/Reveal";
import HeroInteractive from "@/components/HeroInteractive";
import { ReviewHouse } from "@/components/ReviewHouse";
import { ParallaxImage, Spotlight, trackSpotlight, BookPage } from "@/components/motion/Parallax";
import { Seo } from "@/components/Seo";
import { organizationSchema, websiteSchema } from "@/lib/seo";

const GoldLink = ({ to, children }) => (
  <Link to={to} className="group inline-flex items-center text-gold hover:text-gold-dark text-sm md:text-base font-medium transition-colors">
    {children}
    <ChevronRight strokeWidth={2} className="w-4 h-4 mt-0.5 transition-transform group-hover:translate-x-0.5" />
  </Link>
);

function BrandImage() {
  return (
    <div className="relative overflow-hidden rounded-[1.75rem] aspect-[4/3] ring-1 ring-black/10 bg-zinc-100">
      <ParallaxImage src="/custom-3d-print-1400.webp" srcSet="/custom-3d-print-800.webp 800w, /custom-3d-print-1400.webp 1400w" sizes="(min-width: 1400px) 1300px, 100vw" alt="Kann 3D-print configurator op een bureau" range={10} />
    </div>
  );
}

function StandsTile() {
  const { t } = useTranslation();
  return (
    <Reveal y={60} delay={0.15} className="h-full">
      <div onMouseMove={trackSpotlight} data-testid="stands-tile" className="group relative overflow-hidden rounded-[1.75rem] h-[500px] md:h-[560px] ring-1 ring-black/10" style={{ backgroundColor: "#232323" }}>
        {/* subtle light dots (behind image) */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.95) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
            maskImage: "radial-gradient(ellipse 85% 75% at 55% 42%, black, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse 85% 75% at 55% 42%, black, transparent 78%)",
          }}
        />
        {/* soft top light for depth */}
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 30%, rgba(255,255,255,0.12), rgba(35,35,35,0) 70%)" }} />
        <ParallaxImage src="/stands.webp" srcSet="/stands-640.webp 640w, /stands.webp 1100w" sizes="(min-width: 768px) 50vw, 100vw" alt="Sculptural speaker stand" imgClassName="transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
        <Spotlight color="rgba(255,255,255,0.035)" />
        {/* scrim for legibility (matches Mounting tile) */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(35,35,35,0.95) 6%, rgba(35,35,35,0.35) 42%, rgba(35,35,35,0) 100%)" }} />
        <div className="relative z-[2] h-full flex flex-col justify-end p-8 md:p-10 text-white">
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{t("home.standsEyebrow")}</span>
          <h3 className="mt-3 font-display text-2xl md:text-[2rem] font-semibold tracking-tight leading-tight">{t("home.standsTitle")}</h3>
          <p className="mt-2.5 text-sm text-zinc-300 max-w-sm">{t("home.standsBody")}</p>
          <Link
            to="/shop/google-nest"
            data-testid="stands-cta"
            className="group/btn mt-6 inline-flex items-center gap-2 self-start bg-white text-zinc-900 pl-5 pr-4 py-3 rounded-full text-sm font-medium pill-fx"
          >
            {t("home.standsCta")}
            <span className="grid place-items-center w-6 h-6 rounded-full bg-zinc-900/10 group-hover/btn:translate-x-0.5 transition-transform">
              <ArrowRight strokeWidth={2} className="pill-fx-arrow w-3.5 h-3.5" />
            </span>
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function MountingTile() {
  const { t } = useTranslation();
  return (
    <Reveal y={60} delay={0} className="h-full">
      <div onMouseMove={trackSpotlight} data-testid="mounting-tile" className="group relative overflow-hidden rounded-[1.75rem] h-[500px] md:h-[560px] ring-1 ring-black/10" style={{ backgroundColor: "#0A0A0A" }}>
        {/* subtle dotted texture (behind image) */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
            maskImage: "radial-gradient(ellipse 85% 75% at 65% 30%, black, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse 85% 75% at 65% 30%, black, transparent 78%)",
          }}
        />
        {/* warm orange glow (behind image) */}
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(46% 46% at 66% 32%, rgba(219,150,13,0.26), rgba(219,150,13,0.07) 45%, rgba(10,10,10,0) 72%)" }} />
        <ParallaxImage src="/mounting.webp" srcSet="/mounting-640.webp 640w, /mounting.webp 1000w" sizes="(min-width: 768px) 50vw, 100vw" alt="Corner and wall mounting solutions" imgClassName="transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
        <Spotlight />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.92) 4%, rgba(10,10,10,0.15) 46%, rgba(10,10,10,0.55) 100%)" }} />
        <div className="relative z-[2] h-full flex flex-col justify-end p-8 md:p-10 text-white">
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{t("home.mountingEyebrow")}</span>
          <h3 className="mt-3 font-display text-2xl md:text-[2rem] font-semibold tracking-tight leading-tight">{t("home.mountingTitle")}</h3>
          <p className="mt-2.5 text-sm text-zinc-300 max-w-sm">
            {t("home.mountingBody")}
          </p>
          <Link
            to="/shop"
            data-testid="mounting-cta"
            className="group/btn mt-6 inline-flex items-center gap-2 self-start bg-white text-zinc-900 pl-5 pr-4 py-3 rounded-full text-sm font-medium pill-fx"
          >
            {t("home.mountingCta")}
            <span className="grid place-items-center w-6 h-6 rounded-full bg-zinc-900/10 group-hover/btn:translate-x-0.5 transition-transform">
              <ArrowRight strokeWidth={2} className="pill-fx-arrow w-3.5 h-3.5" />
            </span>
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function FlushBoxxTile() {
  const { t } = useTranslation();
  return (
    <BookPage hinge="bottom" className="md:col-span-2">
      <div onMouseMove={trackSpotlight} data-testid="flushboxx-tile" className="group relative overflow-hidden rounded-[1.75rem] grid grid-cols-1 md:grid-cols-2 min-h-[460px] md:min-h-[520px]" style={{ backgroundColor: "#0A0A0A" }}>
        <Spotlight color="rgba(219,150,13,0.05)" />
        {/* image left */}
        <div className="relative order-2 md:order-1 min-h-[300px] md:min-h-full">
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 60% at 50% 55%, rgba(219,150,13,0.10), rgba(10,10,10,0) 70%)" }} />
          <ParallaxImage src="/flushboxx.webp" srcSet="/flushboxx-640.webp 640w, /flushboxx.webp 1100w" sizes="(min-width: 768px) 50vw, 100vw" alt="FlushBoxx recessed housing for motion sensors" range={6} imgClassName="transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
        </div>
        {/* text right */}
        <div className="relative z-[2] order-1 md:order-2 flex flex-col justify-center text-center md:text-left px-8 md:pr-14 md:pl-6 pt-12 md:pt-0 text-white">
          <span className="text-xs uppercase tracking-[0.22em] text-gold font-semibold">{t("home.flushEyebrow")}</span>
          <h3 className="mt-3 font-display text-3xl md:text-[2.75rem] font-semibold tracking-tight leading-[1.05]">
            FlushBoxx<span className="align-super text-base">™</span> {t("home.flushTitle")}
          </h3>
          <p className="mt-4 text-sm md:text-base text-zinc-400 max-w-md mx-auto md:mx-0">
            {t("home.flushBody")}
          </p>
          <div className="mt-8 flex justify-center md:justify-start">
            <Link
              to="/shop/philips-hue"
              data-testid="flushboxx-cta"
              className="group/btn inline-flex items-center gap-2 bg-white text-zinc-900 pl-6 pr-5 py-3.5 rounded-full text-sm font-medium pill-fx"
            >
              {t("home.flushCta")}
              <span className="grid place-items-center w-6 h-6 rounded-full bg-zinc-900/10 group-hover/btn:translate-x-0.5 transition-transform">
                <ArrowRight strokeWidth={2} className="pill-fx-arrow w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </BookPage>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div className="bg-zinc-50">
      <Seo
        title={t("seo.homeTitle")}
        description={t("seo.homeDesc")}
        schemas={[organizationSchema(), websiteSchema()]}
      />
      <HeroInteractive />

      {/* Works with — brand strip */}
      <section className="bg-zinc-50 border-y border-zinc-200/70">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-7 md:py-8">
          <RevealStagger className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 md:gap-x-16" stagger={0.08}>
            <StaggerItem>
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">{t("home.worksWith")}</span>
            </StaggerItem>
            {[
              { name: "Philips Hue", to: "/shop/philips-hue" },
              { name: "Google Nest", to: "/shop/google-nest" },
              { name: "IKEA Home Smart", to: "/shop/ikea-tradfri" },
            ].map((b) => (
              <StaggerItem key={b.name}>
                <Link
                  to={b.to}
                  data-testid={`works-with-${b.name.split(" ")[0].toLowerCase()}`}
                  className="group inline-flex items-center font-display text-lg md:text-xl font-semibold tracking-tight text-zinc-400 hover:text-zinc-900 transition-colors duration-300"
                >
                  {b.name}
                  <span className="ml-0.5 text-gold opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">.</span>
                </Link>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Apple-style bento showcase */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 md:py-24">
        <Reveal>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight text-zinc-900">{t("home.lineupTitle")}</h2>
            <p className="mt-3 text-zinc-500">{t("home.lineupSub")}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FlushBoxxTile />
          <MountingTile />
          <StandsTile />
        </div>
      </section>

      {/* Featured grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-14 md:pb-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">{t("home.bestSellers")}</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-zinc-900">{t("home.popularNow")}</h2>
            </div>
            <GoldLink to="/shop">{t("home.viewAll")}</GoldLink>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Custom 3D printing service */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          {/* Visual */}
          <Reveal y={40}>
            <BrandImage />
          </Reveal>
          {/* Content */}
          <Reveal delay={0.15}>
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-medium">{t("home.storyEyebrow")}</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight text-zinc-900">{t("home.storyTitle")}</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-zinc-500">{t("home.storyBody")}</p>
            <div className="mt-9">
              <GoldLink to="/configurator">{t("home.storyCta")}</GoldLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pb-16 md:pb-24">
          <ReviewHouse
            reviews={[
              { q: t("home.q3"), n: "Fabio" },
              { q: t("home.q2"), n: "Frank" },
              { q: t("home.q1"), n: "Wesley" },
            ]}
          />
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="relative overflow-hidden bg-zinc-950 text-white">
        <div className="pointer-events-none absolute -top-1/3 left-[calc(50%-560px)] w-[520px] h-[520px] rounded-full bg-[#DB960D]/[0.12] blur-[120px] animate-drift-a" />
        <div className="pointer-events-none absolute -bottom-1/3 right-[8%] w-[460px] h-[460px] rounded-full bg-white/[0.06] blur-[110px] animate-drift-b" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "24px 24px", maskImage: "radial-gradient(ellipse 60% 70% at 50% 50%, black, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse 60% 70% at 50% 50%, black, transparent 75%)" }}
        />
        <Reveal className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter">{t("cta.heading")}</h2>
          <p className="mt-5 text-zinc-400 max-w-md mx-auto">{t("cta.subtext")}</p>
          <Link to="/shop" className="group mt-10 inline-flex items-center gap-2 bg-white text-zinc-900 px-8 py-4 rounded-full text-sm font-medium pill-fx" data-testid="home-cta-button">
            {t("home.ctaButton")} <ArrowRight strokeWidth={1.5} className="pill-fx-arrow w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
